import { useState } from "react";
import { cn } from "@ridereserv/shared/utils";
import { datetimeLocalToIso, datetimeLocalNow } from "@/lib/datetime";
import { posthog } from "@/lib/posthog";
import { FormField } from "@/components/ui/FormField";
import { Input } from "@/components/ui/Input";
import type { WidgetRideType } from "./widgetPresets";

interface CompactWidgetTeaserProps {
  /** Company display name for the teaser heading (server data). */
  companyName: string;
  /** The company's IANA timezone — pickup wall time is interpreted in it. */
  timezone: string;
  /** Server-fetched ride types (>= 2 — single-type companies skip the teaser). */
  rideTypes: WidgetRideType[];
  /**
   * Called when the visitor taps a ride-type card. The teaser then expands
   * in-frame into the full wizard with step 1 pre-answered.
   * @param rideTypeId - The tapped ride type's id (from the fetched list).
   * @param pickupDatetime - ISO instant from the date affordance, if entered.
   */
  onSelect: (rideTypeId: string, pickupDatetime?: string) => void;
}

/**
 * The `?layout=compact` mini-form teaser (ADR-011 Decision 3): the company's
 * ride-type cards plus an optional pickup date affordance, condensed enough
 * for a homepage hero or content column. Tapping a card expands within the
 * same iframe into the full booking wizard with the Ride Type step
 * pre-answered — the loader's postMessage auto-resize handles the height
 * change, so the embed contract is unchanged.
 *
 * Presentation-only: no tier or capability logic lives here.
 * @param props - Component props
 * @param props.companyName - Company display name for the heading.
 * @param props.timezone - Company IANA timezone for the date affordance.
 * @param props.rideTypes - Server-fetched ride types to offer.
 * @param props.onSelect - Handoff callback when a card is tapped.
 * @returns The compact teaser mini-form.
 */
export function CompactWidgetTeaser({
  companyName,
  timezone,
  rideTypes,
  onSelect,
}: CompactWidgetTeaserProps) {
  const [dateValue, setDateValue] = useState("");

  const handleSelect = (rideTypeId: string): void => {
    // Funnel event so compact-vs-full conversion is measurable. Names + the
    // validated enum only — no PII, no raw query input.
    posthog.capture("widget_compact_expanded", { widget_layout: "compact" });
    const iso = datetimeLocalToIso(dateValue, timezone);
    onSelect(rideTypeId, iso || undefined);
  };

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-base font-semibold text-neutral-900">Book a ride</h2>
        <p className="text-sm text-neutral-600">
          Pick a service to start your {companyName} booking.
        </p>
      </div>

      <FormField label="Pickup Date & Time" htmlFor="widget-teaser-pickup">
        <Input
          id="widget-teaser-pickup"
          type="datetime-local"
          min={datetimeLocalNow(timezone)}
          value={dateValue}
          onChange={(e) => {
            setDateValue(e.target.value);
          }}
        />
      </FormField>

      <div className="space-y-2">
        {rideTypes.map((rideType) => (
          <button
            key={rideType.id}
            type="button"
            onClick={() => {
              handleSelect(rideType.id);
            }}
            className={cn(
              "w-full rounded-md border border-neutral-200 p-3 text-left transition-colors",
              "hover:border-primary-300",
              "focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1",
              "min-h-11 pointer-fine:min-h-9"
            )}
          >
            <p className="font-medium text-neutral-900">{rideType.name}</p>
            {rideType.description && (
              <p className="mt-0.5 text-sm text-neutral-600">{rideType.description}</p>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
